<script setup lang="ts">
import { computed } from 'vue';
import type { ArtKey } from '../skins/index.js';
import { useSkin } from '../stores/skin.js';

/**
 * 皮肤插画:按键取当前皮肤的资产;装饰性,不进无障碍树。
 * 皮肤清单给了这张图的「点睛层」(artTint:只画点睛那一笔的图)时,在图上叠一层拿它当遮罩的色块,颜色走令牌 --art-tint ——
 * 图是 <img>,读不到页面的主题色;叠这一层,那一笔才能跟着机构主题色走。没给点睛层的图(default 皮肤全部)照原样只出一张图。
 */
/** lazy:首屏以外的大图(场景、封面、徽章)给 true,滚到附近才下载;图的大小一律由外面那层定(宽高或 aspect-ratio),版面不跳 */
const props = defineProps<{ name: ArtKey; alt?: string; lazy?: boolean }>();
const skin = useSkin();
const src = computed(() => skin.art(props.name));
const tint = computed(() => skin.manifest?.artTint?.[props.name] ?? '');
const tintStyle = computed(() => {
  const url = `url("${tint.value}")`;
  return { maskImage: url, WebkitMaskImage: url };
});
</script>

<template>
  <span v-if="src && tint" class="skin-art skin-art-tinted">
    <img
      :src="src"
      :alt="alt ?? ''"
      :aria-hidden="alt ? undefined : 'true'"
      :loading="lazy ? 'lazy' : undefined"
      decoding="async"
      draggable="false"
    />
    <i aria-hidden="true" :style="tintStyle"></i>
  </span>
  <img
    v-else-if="src"
    class="skin-art"
    :src="src"
    :alt="alt ?? ''"
    :aria-hidden="alt ? undefined : 'true'"
    :loading="lazy ? 'lazy' : undefined"
    decoding="async"
    draggable="false"
  />
</template>

<style scoped>
.skin-art {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
}
/* 带点睛层的:外面一层占原来那张图的位置,里面图铺满、点睛层盖在上面(同一个画框,居中等比) */
.skin-art-tinted {
  position: relative;
}
.skin-art-tinted > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
}
.skin-art-tinted > i {
  position: absolute;
  inset: 0;
  background: var(--art-tint, var(--accent));
  -webkit-mask: center / contain no-repeat;
  mask: center / contain no-repeat;
  pointer-events: none;
}
</style>
